import { useState } from "react";
import { useDebounce } from "use-debounce";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, MoreHorizontal, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "@/components/layout/PageTitle";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { recipeRequests } from "@/lib/api/requests/recipes";
import { useAuth } from "@/hooks/useAuth";
import type { StorageContainer, Tool } from "@/types/recipe";

type CatalogTab = "containers" | "tools";
type CatalogItem = StorageContainer | Tool;

const PAGE_SIZE = 20;

interface CatalogForm {
  name: string;
  secondary: string;
  tertiary: string;
}

const EMPTY_FORM: CatalogForm = { name: "", secondary: "", tertiary: "" };

export function EquipmentCatalogPage() {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const canManage =
    hasPermission("recipe:update") || hasPermission("recipe:*") || hasPermission("*:*");
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<CatalogTab>("containers");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [form, setForm] = useState<CatalogForm>(EMPTY_FORM);
  // A catalog item is shared by every recipe that links it, so deleting one is
  // confirmed first.
  const [pendingDelete, setPendingDelete] = useState<CatalogItem | null>(null);

  const queryKey = ["equipment-catalog", tab, debouncedSearch, page];
  const catalogQuery = useQuery({
    queryKey,
    queryFn: () => tab === "containers"
      ? recipeRequests.listStorageContainersCatalog({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined })
      : recipeRequests.listToolsCatalog({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (tab === "containers") {
        if (editing) {
          return recipeRequests.updateStorageContainerCatalog(editing.id, {
            name: form.name.trim(),
            subType: form.secondary.trim() || null,
            sizeOpen: form.tertiary.trim() || null,
          });
        }
        return recipeRequests.createStorageContainerCatalog({
          name: form.name.trim(),
          subType: form.secondary.trim() || undefined,
          sizeOpen: form.tertiary.trim() || undefined,
        });
      }
      // On edit an emptied field is sent as null so it clears, as containers do;
      // on create it is simply left out.
      if (editing) {
        return recipeRequests.updateToolCatalog(editing.id, {
          name: form.name.trim(),
          toolType: form.secondary.trim() || null,
          sizeSpec: form.tertiary.trim() || null,
        });
      }
      return recipeRequests.createToolCatalog({
        name: form.name.trim(),
        toolType: form.secondary.trim() || undefined,
        sizeSpec: form.tertiary.trim() || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["equipment-catalog", tab] });
      void queryClient.invalidateQueries({ queryKey: tab === "containers" ? ["storage-containers"] : ["tools"] });
      setDialogOpen(false);
      toast.success(editing ? t`Item updated` : t`Item added`);
    },
    onError: () => toast.error(editing ? t`Failed to update item` : t`Failed to add item`),
  });

  const deleteMutation = useMutation({
    mutationFn: (item: CatalogItem) => tab === "containers"
      ? recipeRequests.deleteStorageContainerCatalog(item.id)
      : recipeRequests.deleteToolCatalog(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["equipment-catalog", tab] });
      // The recipe pickers cache the catalog too (['storage-containers',
      // 'catalog-picker'], ['tools', 'catalog-picker']); without this they keep
      // offering the deleted item.
      void queryClient.invalidateQueries({ queryKey: tab === "containers" ? ["storage-containers"] : ["tools"] });
      setPendingDelete(null);
      toast.success(t`Item deleted`);
    },
    onError: () => toast.error(t`Item could not be deleted`),
  });

  const rows = catalogQuery.data?.data ?? [];
  const totalPages = catalogQuery.data?.meta.totalPages ?? 0;
  const total = catalogQuery.data?.meta.total ?? 0;

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(item: CatalogItem) {
    setEditing(item);
    setForm({
      name: item.name,
      secondary: tab === "containers" ? (item as StorageContainer).subType ?? "" : (item as Tool).toolType ?? "",
      tertiary: tab === "containers" ? (item as StorageContainer).sizeOpen ?? "" : (item as Tool).sizeSpec ?? "",
    });
    setDialogOpen(true);
  }

  function switchTab(next: CatalogTab) {
    setTab(next);
    setSearch("");
    setPage(1);
  }

  const secondaryLabel = tab === "containers" ? t`Sub-type` : t`Tool type`;
  const tertiaryLabel = tab === "containers" ? t`Open size` : t`Size / spec`;

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <PageTitle size="lg" title={t`Equipment`} />
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm text-(--color-text-secondary)">{t`Manage the containers and tools available to recipes.`}</p>
          <div className="mt-4 flex w-fit rounded-lg border border-(--color-border) bg-(--color-surface-muted)/50 p-1">
            {(["containers", "tools"] as CatalogTab[]).map((item) => (
              <Button
                key={item}
                variant="ghost"
                onClick={() => switchTab(item)}
                className={item === tab ? "bg-(--color-surface-page) text-(--color-text-primary) shadow-xs" : "text-(--color-text-secondary)"}
              >
                {item === "containers" ? t`Storage containers` : t`Tools`}
              </Button>
            ))}
          </div>
        </div>
        {canManage && <Button onClick={openCreate} className="gap-2"><Plus className="size-4" />{tab === "containers" ? t`Add container` : t`Add tool`}</Button>}
      </div>

      <div className="rounded-xl border border-(--color-border) bg-(--color-surface-content)">
        <div className="flex items-center gap-3 border-b border-(--color-border) p-4">
          <Search className="size-4 text-(--color-text-secondary)" />
          <Input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder={t`Search...`} aria-label={t`Search equipment`} className="max-w-sm border-0 p-0 shadow-none focus-visible:ring-0" />
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>{t`Name`}</TableHead><TableHead>{secondaryLabel}</TableHead><TableHead>{tertiaryLabel}</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-40 text-center text-sm text-(--color-text-secondary)">
                  {catalogQuery.isError ? (
                    // An outage is not an empty catalog.
                    <span role="alert" className="flex flex-col items-center gap-2 text-(--color-danger-text)">
                      {t`The catalog couldn't be loaded.`}
                      <Button variant="outline" size="sm" onClick={() => void catalogQuery.refetch()}>
                        {t`Try again`}
                      </Button>
                    </span>
                  ) : catalogQuery.isLoading ? (
                    t`Loading...`
                  ) : (
                    t`No items found.`
                  )}
                </TableCell>
              </TableRow>
            ) : rows.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>{tab === "containers" ? (item as StorageContainer).subType ?? "—" : (item as Tool).toolType ?? "—"}</TableCell>
                <TableCell>{tab === "containers" ? (item as StorageContainer).sizeOpen ?? "—" : (item as Tool).sizeSpec ?? "—"}</TableCell>
                <TableCell>
                  {canManage && <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label={t`Actions`}><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(item)} className="gap-2"><Pencil className="size-4" />{t`Edit`}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPendingDelete(item)} className="gap-2 text-(--color-danger-text)"><Trash2 className="size-4" />{t`Delete`}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between border-t border-(--color-border) px-4 py-3 text-sm text-(--color-text-secondary)">
          <span>{t`Showing ${rows.length} of ${total}`}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label={t`Previous page`}
              disabled={page <= 1 || catalogQuery.isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span>{t`Page ${page} of ${Math.max(1, totalPages)}`}</span>
            <Button
              variant="outline"
              size="icon"
              aria-label={t`Next page`}
              disabled={page >= totalPages || catalogQuery.isFetching || totalPages === 0}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={tab === "containers" ? t`Delete this container?` : t`Delete this tool?`}
        description={t`It is removed from the catalog and from every recipe that uses it.`}
        confirmLabel={t`Delete`}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
        isPending={deleteMutation.isPending}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t`Edit item` : t`Add item`}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5"><Label htmlFor="equipment-name">{t`Name`}</Label><Input id="equipment-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></div>
            <div className="flex flex-col gap-1.5"><Label htmlFor="equipment-secondary">{secondaryLabel}</Label><Input id="equipment-secondary" value={form.secondary} onChange={(event) => setForm({ ...form, secondary: event.target.value })} /></div>
            <div className="flex flex-col gap-1.5"><Label htmlFor="equipment-tertiary">{tertiaryLabel}</Label><Input id="equipment-tertiary" value={form.tertiary} onChange={(event) => setForm({ ...form, tertiary: event.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>{t`Cancel`}</Button><Button onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isPending}>{editing ? t`Save changes` : t`Add item`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}